/**
 * useTaskListRealtime - Realtime centralizado da página de Listagem de Tarefas
 *
 * Mantém a listagem (`/tenant/tasks/list`) sincronizada em tempo real entre
 * abas/usuários quando alguém inicia, pausa, conclui, cancela ou altera uma
 * tarefa do tenant — eliminando a necessidade de F5.
 *
 * MITIGAÇÕES DE PERFORMANCE (todas embutidas desde v1.0.0):
 *  1) Listener ÚNICO em `public.tasks` (filtro por tenant_id). NÃO assina
 *     `task_comments` porque a listagem não exibe comentários e o trigger
 *     de status já atualiza `tasks.updated_at`. Corta ~50% do volume.
 *  2) DEBOUNCE de 300ms: rajadas (ex.: bulk edit de 500 tarefas) viram
 *     UMA única rodada de `invalidateQueries`.
 *  3) PAUSA em background: enquanto a aba está oculta, apenas marca uma
 *     flag de pendência; ao voltar ao foco, dispara 1 invalidação imediata.
 *     Elimina custo de processamento em abas esquecidas.
 *  4) Canal com sufixo `crypto.randomUUID()` para evitar erro
 *     "cannot add postgres_changes callbacks after subscribe()" ao trocar
 *     de rota rapidamente (mesmo padrão de useDealActivitiesRealtime).
 *
 * O fast-path de invalidação local (já presente em `handleChangeStatus` do
 * TaskList) é mantido — este hook funciona como rede de segurança cross-tab.
 *
 * @version 1.0.0
 * RELEASE: 2026-06-10 v1.0.0 - Criação inicial com as 4 mitigações de performance
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

// Logger dedicado deste hook (segue padrão centralizado src/lib/logger.ts)
const logger = createLogger('TaskListRealtime');

/**
 * Chaves de cache da listagem/kanban invalidadas a cada rodada debounced.
 * Mantemos enxuto: apenas o que a página /tenant/tasks/list e o kanban consomem.
 */
const TASK_LIST_QUERY_KEYS: string[] = [
  'tasks-paginated',
  'task-list-kpis',
  'task-kanban-column',
  'task-kanban-counts',
];

/**
 * Assina Realtime na tabela `tasks` filtrado por tenant_id e invalida as
 * queries da listagem com debounce + pausa em background.
 *
 * Deve ser chamado UMA vez no componente raiz da página TaskList.
 *
 * @param tenantId - ID do tenant ativo (null/undefined = não subscreve)
 */
export function useTaskListRealtime(tenantId: string | null | undefined): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    // Sem tenant resolvido: não cria canal
    if (!tenantId) return;

    logger.debug('Iniciando Realtime da listagem de tarefas', { tenantId });

    // ----- Estado interno do debounce + pausa em background -----
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    let pendingInvalidation = false; // marca evento recebido enquanto aba oculta

    /** Executa a invalidação efetiva das queries da listagem (1 rodada) */
    const flushInvalidation = (reason: string) => {
      pendingInvalidation = false;
      logger.debug('Invalidando queries da listagem de tarefas', { reason });
      for (const key of TASK_LIST_QUERY_KEYS) {
        queryClient.invalidateQueries({ queryKey: [key] });
      }
    };

    /**
     * Agenda invalidação com debounce de 300ms.
     * Se a aba estiver oculta, apenas marca a pendência (mitigação 3).
     */
    const scheduleInvalidation = (reason: string) => {
      // MITIGAÇÃO 3: pausa quando aba está em background
      if (document.visibilityState === 'hidden') {
        pendingInvalidation = true;
        return;
      }

      // MITIGAÇÃO 2: debounce de 300ms agrupa rajadas em 1 refetch
      if (debounceTimer !== null) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        flushInvalidation(reason);
      }, 300);
    };

    /** Quando a aba volta ao foco, drena qualquer pendência acumulada */
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && pendingInvalidation) {
        flushInvalidation('visibility-resume');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // ----- Canal Realtime (1 listener apenas — mitigação 1) -----
    // Nome único por montagem evita colisão de canal cacheado pelo supabase-js
    const channelName = `task-list-rt-${tenantId}-${crypto.randomUUID()}`;

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*', // INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'tasks',
          filter: `tenant_id=eq.${tenantId}`,
        },
        (payload) => {
          logger.debug('Realtime: tasks alterado', {
            eventType: payload.eventType,
          });
          scheduleInvalidation(`tasks:${payload.eventType}`);
        }
      )
      .subscribe((status) => {
        logger.debug('Status do canal Realtime da listagem', { status });
      });

    // Cleanup: remove canal, listener de visibilidade e timer pendente
    return () => {
      logger.debug('Removendo Realtime da listagem de tarefas', { tenantId });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (debounceTimer !== null) clearTimeout(debounceTimer);
      supabase.removeChannel(channel);
    };
  }, [tenantId, queryClient]);
}
